import { useEffect } from 'react';
import {
  Check,
  ChevronRightIcon,
  EllipsisVertical,
  Hourglass,
  Loader2,
  type LucideIcon,
  SquareDashed,
  TriangleAlert,
  XCircle,
  XIcon,
} from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipPortal, TooltipTrigger } from '@/components/ui/tooltip';
import { TaskState, UploadFinishedPercentage, UploadFinishingPercentage } from '@/types/uploads';
import { AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { MenuProvider, dropdownPrimitives } from '@/components/menu-renderer';
import { DeleteNodeMenuItem } from '@/components/menu-items/node-menu-items';
import { Button, buttonVariants } from '@/components/ui/button';
import { DAMNodeType } from '@/types/tree';
import { cn } from '@/lib/utils';
import { useTranslations } from 'next-intl';

export const TaskStateBar = ({
  open,
  taskId,
  state,
  title,
  progress,
  onFinished,
  onCancel,
  subTitle,
  subTasks = [],
  disabled = false,
  hideSubTaskMenu = false,
  collapsed = false,
}: {
  open: boolean;
  taskId: string;
  state: TaskState;
  progress: number;
  title: string;
  onFinished?: () => void;
  onCancel?: () => void;
  subTitle?: string;
  disabled?: boolean;
  /** Hides the per-file "..." menu (trash, etc.) — for a version upload (single-file,
   * no per-file actions apply) or a viewer without that permission. */
  hideSubTaskMenu?: boolean;
  // When collapsed, hide the title/subtitle inline and surface it in a tooltip on
  // the icon, leaving just the icon + progress visible.
  collapsed?: boolean;
  subTasks?: Array<{
    subTaskId: string;
    subTaskLabel: string;
    subTaskDescription?: string;
    subTaskProgress?: number;
    errored?: boolean;
    SubTaskIcon: LucideIcon;
  }>;
}) => {
  const tMenu = useTranslations('menu');

  useEffect(() => {
    if ((state === TaskState.Finished || state === TaskState.Cancelled) && onFinished) {
      const timeout = setTimeout(() => {
        onFinished();
      }, 3000);
      return () => clearTimeout(timeout);
    }
  }, [state, onFinished]);

  let Icon: LucideIcon = SquareDashed;
  let backgroundColor;
  const isWaitingForVersion = state === TaskState.WaitingForVersion;
  // A bar is only an accordion when it has sub-tasks to reveal. Bars without
  // sub-tasks (e.g. export bars) render as a plain row — no AccordionItem/Trigger —
  // so there is no chevron, nothing to expand, and nothing to steal the sidebar's
  // single-type Accordion open state from sibling rows.
  const isExpandable = subTasks.length > 0;

  switch (state) {
    case TaskState.Finished:
      Icon = Check;
      backgroundColor = 'bg-status-success';
      break;
    case TaskState.InProgress:
      Icon = Hourglass;
      backgroundColor = 'bg-status-progress';
      break;
    case TaskState.WaitingForVersion:
      Icon = Loader2;
      backgroundColor = 'bg-status-pending';
      break;
    case TaskState.Cancelled:
      Icon = XCircle;
      backgroundColor = 'bg-status-warning';
      break;
    case TaskState.Errored:
      Icon = TriangleAlert;
      backgroundColor = 'bg-status-failure';
      break;
    default:
      backgroundColor = '';
  }

  if (state === TaskState.NotStarted) {
    return null;
  }

  const headerBody = (
    <>
      <div className="flex min-w-0 grow items-center gap-x-2 text-text-inverted">
        <>
          <Icon className={cn('shrink-0 icon-6', isWaitingForVersion && 'animate-spin')} />
          <div className="flex min-w-0 grow items-center justify-between gap-x-2">
            <div className="min-w-0 shrink truncate text-left text-base font-bold" title={title}>
              {title}
            </div>
            {subTitle && (
              <div className="min-w-0 shrink truncate text-[13px] font-semibold" title={subTitle}>
                {subTitle}
              </div>
            )}
          </div>
        </>
      </div>

      <div className="flex shrink-0 items-center gap-x-2">
        {state === TaskState.InProgress && !collapsed && (
          <span className="shrink-0 text-sm text-text-inverted">{progress}%</span>
        )}

        {isExpandable && !disabled && (
          <span
            aria-hidden="true"
            className={cn(
              buttonVariants({
                variant: 'secondary-toolbar',
                size: 'secondary-toolbar',
              }),
              'shrink-0 hover:bg-black/10',
              open && 'rotate-90',
            )}
          >
            <ChevronRightIcon className="icon-6 stroke-text-inverted group-hover/toolbar-button:stroke-text-base" />
          </span>
        )}
      </div>
    </>
  );

  const barHeader = (
    <div
      className={cn('sticky top-0 z-1 flex h-12 w-full items-stretch overflow-hidden border-none!', backgroundColor)}
    >
      {state !== TaskState.Finished && (
        <div
          className={cn(
            'absolute inset-y-0 left-0 z-10 transition-[width] duration-500 ease-in-out',
            state === TaskState.InProgress
              ? 'bg-status-progress-dark'
              : state === TaskState.WaitingForVersion
                ? 'animate-candystripe-move animate-[candystripe-move_10s_linear_infinite] opacity-20 bg-[linear-gradient(-45deg,hsl(var(--ui-separator))_25%,hsl(var(--ui-void-gap))_25%,hsl(var(--ui-void-gap))_50%,hsl(var(--ui-separator))_50%,hsl(var(--ui-separator))_75%,hsl(var(--ui-void-gap))_75%,hsl(var(--ui-void-gap)))] bg-size-[40px_40px]'
                : state === TaskState.Cancelled
                  ? 'bg-status-warning-dark'
                  : progress === UploadFinishedPercentage
                    ? 'bg-status-dark'
                    : 'bg-status-failure-dark',
          )}
          style={{ width: `${progress}%` }}
        />
      )}

      {isExpandable ? (
        <AccordionTrigger
          data-cy={`task-state-bar-trigger-${taskId}`}
          disableWrapper
          headerClassName="w-full"
          disabled={disabled}
          showDisabledMessage={false}
          showChevron={false}
          className="relative z-20 flex min-w-0 grow items-center justify-between gap-x-2 border-none! bg-transparent! px-3"
        >
          {headerBody}
        </AccordionTrigger>
      ) : (
        <div
          data-cy={`task-state-bar-trigger-${taskId}`}
          className="relative z-20 flex min-w-0 grow items-center justify-between gap-x-2 border-none! bg-transparent! px-3"
        >
          {headerBody}
        </div>
      )}

      {onCancel && (
        <div className="z-20 flex shrink-0 items-center pr-2">
          <Button
            onClick={onCancel}
            className="shrink-0 hover:bg-black/10"
            variant="secondary-toolbar"
            size="secondary-toolbar"
            tooltip={
              state === TaskState.Finished ||
              (state === TaskState.Errored && progress === UploadFinishedPercentage) ||
              state === TaskState.Cancelled
                ? tMenu('task.remove')
                : tMenu('task.cancel')
            }
            icon={XIcon}
            iconClassName="icon-6 stroke-text-inverted group-hover/toolbar-button:stroke-text-base"
            data-cy={`task-state-bar-cancel-${taskId}`}
          />
        </div>
      )}
    </div>
  );

  const barElement = !isExpandable ? (
    <div className="flex flex-col space-y-px">{barHeader}</div>
  ) : (
    <AccordionItem value={taskId} disabled={disabled} className="flex flex-col">
      {barHeader}
      <AccordionContent className="flex flex-col gap-y-1 border-none! bg-ui-panel px-3 py-2">
        {subTasks.map(({ subTaskId, subTaskLabel, subTaskProgress, SubTaskIcon, errored, subTaskDescription }) => {
          return (
            <div key={subTaskLabel} className="flex items-center justify-between">
              <div className="flex min-w-0 flex-1 items-center gap-x-2">
                <SubTaskIcon className="icon-6 text-icon-base" />
                <div className="flex min-w-0 flex-col">
                  <div title={subTaskLabel} className="truncate text-[13px] font-bold text-text-base">
                    {subTaskLabel}
                  </div>
                  {subTaskDescription && (
                    <div title={subTaskDescription} className="truncate text-[11px] text-text-gray">
                      {subTaskDescription}
                    </div>
                  )}
                </div>
              </div>
              <div className="ml-2 max-h-6 shrink-0">
                {errored ? (
                  <div className="flex items-center justify-center px-1">
                    <TriangleAlert className="icon-6 text-icon-base" />
                  </div>
                ) : subTaskProgress === UploadFinishingPercentage ? (
                  <div className="flex items-center justify-center px-1">
                    <Loader2 className="icon-6 animate-spin text-icon-base" />
                  </div>
                ) : subTaskProgress === UploadFinishedPercentage ? (
                  hideSubTaskMenu ? null : (
                    <DropdownMenu>
                      <DropdownMenuTrigger
                        data-cy={`task-state-bar-${taskId}-subtask-dropdown-trigger-${subTaskId}`}
                        asChild
                      >
                        <Button
                          variant="ghost"
                          className="min-h-0 px-1 py-0"
                          icon={EllipsisVertical}
                          iconClassName="icon-6 text-icon-base"
                        />
                      </DropdownMenuTrigger>
                      {/* TODO: Add the other options (download) at some point */}
                      <DropdownMenuContent>
                        <MenuProvider primitives={dropdownPrimitives}>
                          <DeleteNodeMenuItem
                            node={{ id: subTaskId, type: DAMNodeType.Asset, node: { name: subTaskLabel } }}
                            targetsSingleNode
                            label={tMenu('asset.trash')}
                            danger
                          />
                        </MenuProvider>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )
                ) : (
                  subTaskProgress !== undefined && (
                    <div className="flex items-center justify-center px-1 text-text-gray-alternate">
                      <p className="icon-6">{subTaskProgress}%</p>
                    </div>
                  )
                )}
              </div>
            </div>
          );
        })}
      </AccordionContent>
    </AccordionItem>
  );

  return collapsed ? (
    <Tooltip>
      <TooltipTrigger asChild>{barElement}</TooltipTrigger>
      <TooltipPortal>
        <TooltipContent className="max-w-64 break-all">
          {title} - {progress}%
        </TooltipContent>
      </TooltipPortal>
    </Tooltip>
  ) : (
    barElement
  );
};
